import React, { useContext, useState, useEffect } from 'react';
import { useHistory } from "react-router-dom";
import { Link, CircularProgress } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import moment from 'moment';
import { Loading, request, AuthStatusContext } from '@trp-individual-investor-adv-disc/adv-shared';
import NumberFormat from 'react-number-format';

import InfoIcon from '../../../images/InformationIcon.svg';
import TransferContext from '../../../containers/TransferContainer/context';
import Step from '../Step/Step';
import Modal from '../../shared/Modal';
import TransferTable from '../TransferTable/TransferTable';
import ConfirmationTable from '../ConfirmationTable/ConfirmationTable';
import AddAccounts from '../AddAccounts/AddAccounts';
import dispatchOverlayLoad from '../../../utils/dispatchOverlayLoad';
import { stripIsTranferKeysAndHash, getIsTranferSelections } from '../TransferUtils';
import { balancesModalContent, cancelModalContent, tRoweFormatted } from '../../../utils/Content';
import BeaconWrapper from '../BeaconWrapper/BeaconWrapper';
import './AgreementSelection.scss'

const AgreementSelection = ({requestConfig, transfersLoaded}) => {

    const { status } = useContext(AuthStatusContext).state;
    const { isLoggedInUserPOA } = status;
    const { state, dispatch } = useContext(TransferContext);
    const { agreementsGroupByType } = state;
    const history = useHistory();

    const showAutoTransfer = agreementsGroupByType && agreementsGroupByType.length > 0 && agreementsGroupByType.some(group => group.existingAgreement &&
        Object.keys(group.existingAgreement).length && group.agreements.length);
    const selectionMade = agreementsGroupByType && agreementsGroupByType.length > 0 &&
        agreementsGroupByType.some(group => group.agreements.some(agreement => agreement.isTransfer));
    const transferAgreements = agreementsGroupByType.map(agreementGroup => agreementGroup.agreements.filter(agr => agr.isTransfer)).flat();

    const total = transferAgreements && transferAgreements
        .flat().map(agr => agr && agr.holdings)
        .flat().reduce((agg, holding) => agg + (holding && holding.amount), 0)
        .toFixed(2);
    const numberOfAccounts = transferAgreements && transferAgreements
        .flat().length;

    const [isLoading, setIsLoading] = React.useState(false);
    const [step, setStep] = useState(1);
    const [openBalanceModal, setOpenBalModal] = React.useState(false);
    const [openCancelModal, setOpenCancelModal] = React.useState(false);
    const showBalanceModal = () => setOpenBalModal(true);
    const hideBalanceModal = () => setOpenBalModal(false);
    const showCancelModal = () => {
        setOpenCancelModal(true);
        dispatchOverlayLoad("RASCancelTransferConfirm");
    }
    const hideCancelModal = () => setOpenCancelModal(false);
    const redirectToAccountAccess = () => window && window.location && window.location.replace('/usis/my-accounts/dashboard');
    const agreementSelectionStorage = window.sessionStorage;

    const handleSubmit = () => {
        if (process.env.REACT_APP_MOCK) {
            dispatch({
                type: 'UPDATE_SUBMITTED_STATUS'
            });
            agreementSelectionStorage.setItem('stepTwoActive', undefined);
            agreementSelectionStorage.setItem('storedAgreementsGroupByType', undefined);
            agreementSelectionStorage.setItem('storedActiveIndexes', undefined);
            history.push('/confirmation');
        } else {
            setIsLoading(true);
            const transferPayload = state;
            delete transferPayload.submitted;
            request(
                requestConfig,
                '/adv-disc-svc/service/v1/transfer',
                'post',
                {},
                transferPayload,
                '/usis/advice/checkout'
            )
            .then(() => {
                dispatch({
                    type: 'UPDATE_SUBMITTED_STATUS'
                });
                agreementSelectionStorage.setItem('stepTwoActive', undefined);
                agreementSelectionStorage.setItem('storedAgreementsGroupByType', undefined);
                agreementSelectionStorage.setItem('storedActiveIndexes', undefined);
                history.push('/confirmation');
                setIsLoading(false);
            });
        }
    }

    const assetsAsOfDate = agreementsGroupByType.reduce((acc, curr) => {
        acc.push(curr.agreements);
        return acc
    }, [])
        .reduce((ac, curr) => {
            ac.push(curr.map(agreement => agreement.holdings));
            return ac.flat(2);
        }, [])
        .reduce((acc, curr) => {
            const currentDate = curr.asOfDate ? moment(curr.asOfDate) : acc;
            return currentDate.isBefore(acc) ? moment(curr.asOfDate) : acc;
        }, moment().subtract(1, 'days'))
        .format('l');

    useEffect(() => {
        if (!selectionMade && stripIsTranferKeysAndHash(agreementsGroupByType) === agreementSelectionStorage.getItem('storedAgreementsGroupByType')) {
            const storedActiveIndexes = JSON.parse(agreementSelectionStorage.getItem('storedActiveIndexes'));
            if (storedActiveIndexes && storedActiveIndexes.length) {
                storedActiveIndexes.forEach(indexSet => {
                    // refactor reducer to handle array for indexes rather than calling multiple times
                    dispatch({
                        type: 'UPDATE_TRANSFER_AGREEMENTS',
                        payload: {
                            groupIndex: indexSet.groupIndex,
                            agreementIndex: indexSet.agreementIndex
                        }
                    });
                });
                if (agreementSelectionStorage.getItem('stepTwoActive') === 'true' && step === 1) {
                    setStep(2);
                    history.replace("/step-2");
                }
            }
        } else if (!selectionMade) {
            history.replace("/");
        }
    }, [agreementsGroupByType]);

    const handleSubmitStepOne = () => {
        agreementSelectionStorage.setItem('stepTwoActive', 'true');
        agreementSelectionStorage.setItem('storedAgreementsGroupByType', stripIsTranferKeysAndHash(agreementsGroupByType));
        agreementSelectionStorage.setItem('storedActiveIndexes', JSON.stringify(getIsTranferSelections(agreementsGroupByType)));
        setStep(2);
        history.replace("/step-2");
    }

    const handleEdit = () => {
        agreementSelectionStorage.setItem('stepTwoActive', undefined);
        agreementSelectionStorage.setItem('storedAgreementsGroupByType', undefined);
        agreementSelectionStorage.setItem('storedActiveIndexes', undefined);
        setStep(1);
        history.replace("/");
    };

    return (
        <>
            <Modal
                maxWidth='md'
                modalClassName="balance-modal"
                headerClassName="balance-modal-header"
                headerText={balancesModalContent.header}
                open={openBalanceModal}
                onClickHandler={hideBalanceModal}
                showCloseIcon
                data-metrics-location="PU"
            >
                <div className="balance-modal-content"
                    // eslint-disable-next-line
                    dangerouslySetInnerHTML={{
                        __html:
                            balancesModalContent.content,
                    }} />
            </Modal>
            <Modal
                maxWidth='md'
                modalClassName="balance-modal"
                headerClassName="balance-modal-header"
                headerText={cancelModalContent.header}
                open={openCancelModal}
                onClickHandler={hideCancelModal}
                showCloseIcon
                data-metrics-location="PU"
            >
                <div className="balance-modal-content"
                    // eslint-disable-next-line
                    dangerouslySetInnerHTML={{
                        __html:
                            cancelModalContent.content,
                    }} />
                <div className='cancel-button-wrapper'>
                    <BeaconWrapper>
                        <button type='button'
                            className='secondary modal-button transfer-button'
                            onClick={redirectToAccountAccess}
                        >
                            Leave This Page
                        </button>
                        <button type='button'
                            className='primary modal-button'
                            onClick={hideCancelModal}
                        >
                            Stay on This Page
                        </button>
                    </BeaconWrapper>
                </div>
            </Modal>
            {transfersLoaded ?
                <>
                    <Step
                        currentStep={step}
                        stepNumber={1}
                        title='Choose your account(s) to transfer'
                        activeContent={
                            <>
                                {showAutoTransfer ?
                                    <div className='stepper-content'>
                                        <p style={{ paddingBottom: '32px' }}>
                                            The following {tRoweFormatted} accounts are currently included in your financial plan and are ready to be
                                            transferred to your advisory accounts in the Retirement Advisory Service. Please select those you would like to transfer.
                                        </p>
                                        <TransferTable agreementsGroupByType={agreementsGroupByType} />
                                    </div>
                                    : null}
                                <AddAccounts isLoggedInUserPOA={isLoggedInUserPOA} />
                            </>
                        }
                        inactiveContent={
                            <p className='selection-summary-container'>
                                {/* eslint-disable-next-line */}
                                <NumberFormat className='blocked_data' value={total} displayType='text' thousandSeparator prefix='$'/> selected from {numberOfAccounts} accounts <Link underline='none' style={{ paddingLeft: '8px', cursor: 'pointer', fontWeight: 'bold', color: '#086ea1' }} onClick={() => handleEdit()}>Edit</Link>
                            </p>
                        }
                    />
                    <Step
                        currentStep={step}
                        setCurrentStep={setStep}
                        stepNumber={2}
                        title='Verify and submit eligible accounts for automated transfers'
                        activeContent={
                            <>
                                <div style={{padding: '32px 32px 16px 32px'}}>Great, these transfers don’t require any additional steps. Simply click “Submit”.</div>
                                <ConfirmationTable agreementsGroupByType={agreementsGroupByType} />
                            </>
                        }
                    />
                    <div className='transfer-button-container'>
                        <button
                            type='button'
                            className='secondary transfer-button'
                            onClick={showCancelModal}
                        >
                            Cancel
                        </button>
                        <button
                            type='button'
                            className={`primary transfer-button ${!selectionMade || isLoading ? 'transfer-button-disabled' : ''}`}
                            disabled={!selectionMade || isLoading}
                            onClick={() => step === 2 ? handleSubmit() : handleSubmitStepOne()}
                        >
                            {step === 2 ? 'Submit' : 'Next: Confirm'} {' '}
                        {isLoading && React.cloneElement(
                            <CircularProgress
                                size={20}
                                id='loading-spinner'
                                style={{
                                    color: '#05C3DE',
                                    marginLeft: '16px',
                                    verticalAlign: 'sub'
                                }}
                            />)}
                        </button>
                    </div>
                    <div className='balance-footer'>
                        <div style={{paddingLeft: '16px'}}>
                            <span>
                                *Balances as of {assetsAsOfDate}
                            </span>
                            <IconButton size="small" aria-label="close" color="inherit" onClick={showBalanceModal}>
                                <img src={InfoIcon} style={{ paddingLeft: '12px' }} alt='chart-bubble' id='chat-bubble' />
                            </IconButton>
                        </div>
                    </div>
                </>
                : <Loading />}
        </>
    );
};

export default AgreementSelection;
